/**
 * Where a person is in Shekko, as a web address (FB-1122, decision 201).
 *
 * The sections used to be app state only, so the address never changed and
 * the browser's Back left Shekko for whatever the tab held before — for the
 * client, an error page. Now every section and every tab inside one has its
 * own address: Back and Forward walk Shekko's own screens, a reload keeps the
 * place, and a phone's back button will do the same.
 *
 * Pure, so the two directions are proved against each other in a unit test.
 * What is *inside* a screen — an open sale, a style, a dialog — is not in the
 * address yet; that is the second step, and mostly matters on phones.
 */

export type View =
  'register' | 'sales' | 'online' | 'customers' | 'repairs' | 'stock' | 'reports' | 'settings';

export type Place = {
  view: View;
  /** A tab inside the section, or null for the section's own start. */
  tab: string | null;
};

/** The address of each section. Stock is *Inventory* on screen and in the address. */
const PATHS: Record<View, string> = {
  register: '/',
  sales: '/sales',
  online: '/sales/online',
  customers: '/customers',
  repairs: '/repairs',
  stock: '/inventory',
  reports: '/reports',
  settings: '/settings',
};

/**
 * The tab a section opens on. Its address is the section's own — `/reports`,
 * not `/reports/end-of-day` — so the plain address and the tab press agree.
 */
const DEFAULT_TABS: Partial<Record<View, string>> = {
  stock: 'products',
  reports: 'endOfDay',
  settings: 'store',
};

/** Sections whose tabs are part of the address. */
const TABBED: ReadonlySet<View> = new Set(['stock', 'reports', 'settings']);

function kebab(key: string): string {
  return key.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);
}

function camel(segment: string): string {
  return segment.replace(/-([a-z])/g, (_, letter: string) => letter.toUpperCase());
}

export function pathOf(place: Place): string {
  const base = PATHS[place.view];
  if (!TABBED.has(place.view) || place.tab === null) return base;
  if (place.tab === DEFAULT_TABS[place.view]) return base;
  return `${base}/${kebab(place.tab)}`;
}

/**
 * The place an address names. Anything unknown is the register — the home
 * every other route already returns to — never an empty screen.
 */
export function placeFromPath(pathname: string): Place {
  const clean = pathname.replace(/\/+$/, '') || '/';
  const exact = (Object.entries(PATHS) as [View, string][]).find(([, path]) => path === clean);
  if (exact !== undefined) return { view: exact[0], tab: null };
  for (const view of TABBED) {
    const base = PATHS[view];
    if (!clean.startsWith(`${base}/`)) continue;
    const rest = clean.slice(base.length + 1);
    if (rest === '' || rest.includes('/') || !/^[a-z0-9-]+$/.test(rest)) break;
    return { view, tab: camel(rest) };
  }
  return { view: 'register', tab: null };
}

/** The tab a section is showing: the one in its place, or the one it opens on. */
export function tabOf(place: Place, view: View): string | null {
  if (place.view !== view) return null;
  return place.tab ?? DEFAULT_TABS[view] ?? null;
}
